import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Loader2, Search, Tag, Edit, Trash2, Plus, Check, X, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainOnlyFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  created_at: string;
  post_count?: number;
}

export const CategoryList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Récupérer directement les catégories du domaine actuel seulement
      let categoriesQuery = supabase
        .from('blog_categories')
        .select('*')
        .order('name');

      // Appliquer le filtre domaine uniquement
      categoriesQuery = applyDomainOnlyFilterToQuery(categoriesQuery);

      if (searchQuery) {
        categoriesQuery = categoriesQuery.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      const { data: categoriesData, error } = await categoriesQuery;

      if (error) throw error;

      // Si aucune catégorie n'existe pour ce domaine, retourner un tableau vide
      if (!categoriesData || categoriesData.length === 0) {
        setCategories([]);
        return;
      }

      // Récupérer le nombre d'articles par catégorie
      const categoriesWithCounts = await Promise.all(
        categoriesData.map(async (category) => {
          const { count, error: countError } = await supabase
            .from('blog_post_categories')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', category.id);

          if (countError) {
            console.error('Erreur lors du comptage des articles:', countError);
            return { ...category, post_count: 0 };
          }

          return { ...category, post_count: count || 0 };
        })
      );

      setCategories(categoriesWithCounts);
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les catégories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [searchQuery]);

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    setDeletingCategory(true);
    try {
      // Vérifier si la catégorie est utilisée
      const { count, error: countError } = await supabase
        .from('blog_post_categories')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', categoryToDelete.id);

      if (countError) throw countError;

      if (count && count > 0) {
        toast({
          variant: 'destructive',
          title: 'Impossible de supprimer',
          description: `Cette catégorie est utilisée par ${count} article(s). Veuillez d'abord retirer ces articles de la catégorie.`,
        });
        setDeleteDialogOpen(false);
        setDeletingCategory(false);
        return;
      }

      // Supprimer la catégorie
      const { error } = await supabase
        .from('blog_categories')
        .delete()
        .eq('id', categoryToDelete.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'La catégorie a été supprimée',
      });

      // Mettre à jour la liste des catégories
      setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de supprimer la catégorie',
      });
    } finally {
      setDeletingCategory(false);
    }
  };

  const handleEditClick = (category: Category) => {
    setCategoryToEdit(category);
    setEditName(category.name);
    setEditDescription(category.description || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!categoryToEdit) return;
    if (!editName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Le nom de la catégorie ne peut pas être vide',
      });
      return;
    }
    
    setSavingCategory(true);
    try {
      const slug = editName
        .toLowerCase()
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, '-');

      const { error } = await supabase
        .from('blog_categories')
        .update({
          name: editName.trim(),
          description: editDescription.trim(),
          slug: `${slug}-${categoryToEdit.id.slice(0, 8)}`,
        })
        .eq('id', categoryToEdit.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'La catégorie a été mise à jour',
      });

      // Mettre à jour la liste des catégories
      setCategories(categories.map(cat => 
        cat.id === categoryToEdit.id 
          ? { 
              ...cat, 
              name: editName.trim(), 
              description: editDescription.trim(),
              slug: `${slug}-${categoryToEdit.id.slice(0, 8)}`
            } 
          : cat
      ));
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de mettre à jour la catégorie',
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Le nom de la catégorie ne peut pas être vide',
      });
      return;
    }
    
    setCreatingCategory(true);
    try {
      const categoryId = uuidv4();
      const slug = newCategoryName
        .toLowerCase()
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, '-');

      // Récupérer le domaine actuel
      const { domain, tenantId } = getDomainFilterParams();
      
      // 🔍 DEBUG - Vérifier le cache localStorage
      const cachedTenant = localStorage.getItem('tenant_cache');
      console.log('🔍 CategoryList - Cache localStorage:', cachedTenant);
      console.log('🔍 CategoryList - domain:', domain);
      console.log('🔍 CategoryList - tenantId:', tenantId);

      const insertData = {
        id: categoryId,
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim(),
        slug: `${slug}-${categoryId.slice(0, 8)}`,
        domain: domain, // Ajouter le domaine actuel
        created_at: new Date().toISOString(),
      };
      
      console.log('🔍 CategoryList - Données AVANT insertion:', insertData);

      const { error } = await supabase.from('blog_categories').insert(insertData);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Catégorie créée avec succès',
      });

      // Ajouter la nouvelle catégorie à la liste
      const newCategory = {
        id: categoryId,
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim(),
        slug: `${slug}-${categoryId.slice(0, 8)}`,
        created_at: new Date().toISOString(),
        post_count: 0
      };
      
      setCategories([...categories, newCategory]);
      
      // Réinitialiser le formulaire
      setNewCategoryName('');
      setNewCategoryDescription('');
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de créer la catégorie',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Catégories de blog</h1>
            <p className="text-gray-400">Gérez les catégories pour vos articles de blog</p>
          </div>

          <Button
            variant="default"
            onClick={() => {
              setNewCategoryName('');
              setNewCategoryDescription('');
              setCreateDialogOpen(true);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="mr-2 h-4 w-4" />
            Ajouter une catégorie
          </Button>
        </div>

        {/* Barre de recherche */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
              placeholder="Rechercher une catégorie..."
            />
          </div>
        </div>

        {/* Liste des catégories */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
            </div>
          ) : categories.length === 0 ? (
            <div className="p-12 text-center">
              <Tag className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Aucune catégorie</h3>
              <p className="text-gray-400">
                {searchQuery
                  ? 'Aucune catégorie ne correspond à votre recherche'
                  : 'Vous n\'avez créé aucune catégorie pour le moment'}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setNewCategoryName('');
                  setNewCategoryDescription('');
                  setCreateDialogOpen(true);
                }}
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Créer une catégorie
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Articles
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date de création
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {categories.map((category, index) => (
                    <tr key={category.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-yellow-400/10 rounded-full flex items-center justify-center">
                            <Tag className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{category.name}</div>
                            <div className="text-sm text-gray-400">/{category.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white line-clamp-1">{category.description || 'Pas de description'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className="bg-blue-400/20 text-blue-400 border-blue-400/20">
                          {category.post_count} article{category.post_count !== 1 ? 's' : ''}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {format(new Date(category.created_at), 'dd MMM yyyy', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(category)}
                            className="text-yellow-400 hover:text-yellow-300"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(category)}
                            className="text-red-400 hover:text-red-300"
                            disabled={category.post_count !== undefined && category.post_count > 0}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialogue de création de catégorie */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Créer une nouvelle catégorie</DialogTitle>
            <DialogDescription className="text-gray-400">
              Ajoutez une nouvelle catégorie pour organiser vos articles de blog
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-gray-300">
                Nom de la catégorie
              </label>
              <Input
                id="name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
                placeholder="Ex: Actualités"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium text-gray-300">
                Description (optionnelle)
              </label>
              <Input
                id="description"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                className="bg-white/5 border border-white/10 text-white"
                placeholder="Une brève description de cette catégorie"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim() || creatingCategory}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {creatingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Créer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de modification de catégorie */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Modifier la catégorie</DialogTitle>
          </DialogHeader>

          {categoryToEdit && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label htmlFor="edit-name" className="text-sm font-medium text-gray-300">
                  Nom de la catégorie
                </label>
                <Input
                  id="edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="bg-white/5 border border-white/10 text-white"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="edit-description" className="text-sm font-medium text-gray-300">
                  Description (optionnelle)
                </label>
                <Input
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="bg-white/5 border border-white/10 text-white"
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={!editName.trim() || savingCategory}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {savingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Supprimer la catégorie</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {categoryToDelete && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center">
                <Tag className="h-5 w-5 text-yellow-400 mr-2" />
                <span className="font-medium text-white">{categoryToDelete.name}</span>
              </div>
              {categoryToDelete.description && (
                <p className="mt-2 text-sm text-gray-400">{categoryToDelete.description}</p>
              )}
              {categoryToDelete.post_count !== undefined && categoryToDelete.post_count > 0 && (
                <div className="mt-4 flex items-center text-red-400 bg-red-400/10 p-3 rounded-lg">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  <p className="text-sm">
                    Cette catégorie contient {categoryToDelete.post_count} article{categoryToDelete.post_count !== 1 ? 's' : ''}. 
                    Vous devez d'abord supprimer ou recatégoriser ces articles.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingCategory || Boolean(categoryToDelete?.post_count && categoryToDelete.post_count > 0)}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CategoryList;
